/**
 * Settings screen — minimal, original.
 * Volume slider, language indicator, logout, push notifications (timbre).
 */
import { useEffect, useRef, useState } from 'react';
import { useApp } from '@/state/store';
import { sfx, setMuted, setVolume, getMuted, getVolume } from '@/lib/audio';
import { GearSprite, CheckSprite, XSprite } from '@/components/Sprites';
import { PushToggle } from '@/components/PushToggle';

export function SettingsScreen() {
  const { logout, user, showToast, goto } = useApp();
  const [muted, setMutedState] = useState(getMuted());
  const [vol, setVol] = useState(getVolume());
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  const toggleMute = () => {
    const next = !muted;
    setMutedState(next);
    setMuted(next);
    sfx.tick();
  };

  const changeVol = (delta: number) => {
    const next = Math.max(0, Math.min(1, vol + delta));
    setVol(next);
    setVolume(next);
    sfx.tick();
  };

  const doLogout = () => {
    sfx.confirm();
    logout();
    showToast('Sesión cerrada', 'info');
  };

  // A = primary action, B = back/cancel
  const handlersRef = useRef({ confirmingLogout, setConfirmingLogout, doLogout, toggleMute, changeVol, vol, goto });
  handlersRef.current = { confirmingLogout, setConfirmingLogout, doLogout, toggleMute, changeVol, vol, goto };

  useEffect(() => {
    const onA = () => {
      const h = handlersRef.current;
      if (h.confirmingLogout) {
        h.doLogout();
      } else {
        h.setConfirmingLogout(true);
      }
    };
    const onB = () => {
      const h = handlersRef.current;
      if (h.confirmingLogout) { sfx.cancel(); h.setConfirmingLogout(false); return; }
      h.goto('home');
    };
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    return () => {
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
    };
  }, []);

  if (confirmingLogout) {
    return (
      <div className="gb-screen">
        <div className="gb-status"><span className="gb-pix gb-pix-xs">CERRAR SESIÓN</span></div>
        <div className="gb-body">
          <div className="gb-center" style={{ gap: 10 }}>
            <div className="gb-pix gb-pix-md" style={{ textAlign: 'center' }}>¿CERRAR SESIÓN?</div>
            <div className="gb-dialog" style={{ maxWidth: 200, fontSize: 6, textAlign: 'center' }}>
              Tendrás que volver a entrar con tu email.
            </div>
            <div className="gb-row" style={{ gap: 6 }}>
              <button
                onClick={() => { sfx.cancel(); setConfirmingLogout(false); }}
                className="gb-action"
                style={{ fontSize: 7, padding: '4px 8px', background: 'var(--gb-mid)' }}
              >
                [B] NO
              </button>
              <button
                onClick={doLogout}
                className="gb-action"
                style={{ fontSize: 7, padding: '4px 8px' }}
                data-testid="settings-confirm-logout"
              >
                <CheckSprite size={10} /> SÍ
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <GearSprite size={11} />
          <span className="gb-pix gb-pix-xs">AJUSTES</span>
        </div>
      </div>
      <div className="gb-body">
        <div className="gb-stack" style={{ gap: 8 }}>
          <div>
            <div className="gb-pix gb-pix-xs" style={{ marginBottom: 4 }}>VOLUMEN</div>
            <div className="gb-row" style={{ gap: 6, alignItems: 'center' }}>
              <button onClick={() => changeVol(-0.1)} className="gb-action" style={{ fontSize: 7, padding: '4px 8px' }}>−</button>
              <div className="gb-bar" style={{ flex: 1, height: 8 }}>
                <div className="gb-bar-fill" data-stripe="true" style={{ transform: `scaleX(${vol})` }} />
              </div>
              <button onClick={() => changeVol(0.1)} className="gb-action" style={{ fontSize: 7, padding: '4px 8px' }}>+</button>
              <div className="gb-pix gb-pix-xs" style={{ minWidth: 28 }}>{Math.round(vol * 100)}%</div>
            </div>
          </div>

          <div>
            <div className="gb-pix gb-pix-xs" style={{ marginBottom: 4 }}>TIMBRE</div>
            <PushToggle showToast={showToast} />
          </div>

          <div>
            <div className="gb-pix gb-pix-xs" style={{ marginBottom: 4 }}>SONIDO</div>
            <button
              onClick={toggleMute}
              className="gb-menu-item"
              data-selected={false}
            >
              <span className="gb-menu-num">·</span>
              <span style={{ flex: 1, textAlign: 'left' }}>{muted ? 'SILENCIO' : 'ACTIVADO'}</span>
              <span className="gb-menu-arrow">{muted ? <XSprite size={8} /> : <CheckSprite size={8} />}</span>
            </button>
          </div>

          <div className="gb-divider" />

          <div>
            <div className="gb-pix gb-pix-xs" style={{ marginBottom: 4 }}>IDIOMA</div>
            <div className="gb-menu-item" data-selected={false}>
              <span className="gb-menu-num">·</span>
              <span style={{ flex: 1, textAlign: 'left' }}>ESPAÑOL</span>
            </div>
          </div>

          <div className="gb-divider" />

          <div>
            <div className="gb-pix gb-pix-xs" style={{ marginBottom: 4 }}>CUENTA</div>
            <div className="gb-menu-item" data-selected={false}>
              <span className="gb-menu-num">·</span>
              <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.email}
              </span>
            </div>
            <button
              onClick={() => { sfx.tick(); setConfirmingLogout(true); }}
              className="gb-action"
              style={{ marginTop: 8, alignSelf: 'center', fontSize: 7, padding: '4px 10px', background: 'var(--gb-mid)' }}
              data-testid="settings-logout"
            >
              CERRAR SESIÓN [A]
            </button>
          </div>

          <div className="gb-divider" />

          <div>
            <div className="gb-pix gb-pix-xs gb-mid-text" style={{ marginBottom: 4, textAlign: 'center' }}>
              ¿NECESITÁS MÁS?
            </div>
            <a
              href="https://vicin.app"
              target="_blank"
              rel="noopener noreferrer"
              className="gb-action"
              style={{
                display: 'inline-flex',
                marginTop: 4,
                alignSelf: 'center',
                fontSize: 7,
                padding: '4px 10px',
                background: 'var(--gb-mid)',
                textDecoration: 'none',
                color: 'var(--gb-bg)',
              }}
              data-testid="settings-pro-link"
            >
              VICIN PRO &gt;
            </a>
            <div className="gb-pix gb-pix-xs gb-mid-text" style={{ marginTop: 4, textAlign: 'center', fontSize: 5 }}>
              Webapp completa
            </div>
          </div>
        </div>

        <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, textAlign: 'center', marginTop: 'auto', paddingTop: 4, display: 'none' }}>
          [B] VOLVER
        </div>
      </div>
    </div>
  );
}
